import { useEffect, useMemo, useState } from "react";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import {
    Button,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner,
} from "reactstrap";
import useCreateDataFlow from "../hooks/useCreateDataFlow";
import useDataFlowOptions from "../hooks/useDataFlowOptions";
import {
    DIRECTION_RECEIVES,
    buildCreatePayload,
    isHardwareCounterpart,
    isReceivesFromHardware,
    resolveHardwareRelation,
    toOptionValues,
    toSelectOptions,
    withRelationOptionAvailability,
} from "../helpers/flowForm";
import CounterpartPicker from "./CounterpartPicker";
import DataFlowFormFields from "./DataFlowFormFields";

const EMPTY_FORM = {
    selectedCounterpart: null,
    purpose: "",
    notes: "",
    dataTypes: [],
    dataCategories: [],
    methods: [],
    relation: null,
};

const CreateDataFlowModal = ({
    isOpen,
    direction,
    softwareId,
    wrapperAssetId,
    isResolvingWrapper = false,
    onClose,
    t,
}) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});

    const { data: options = {} } = useDataFlowOptions({ t });
    const { createFlow, isSaving } = useCreateDataFlow({
        softwareId,
        t,
        onSuccess: onClose,
        onError: setErrors,
    });

    const dataTypeOptions = useMemo(
        () => toSelectOptions(options.data_types, t),
        [options.data_types, t]
    );
    const dataCategoryOptions = useMemo(
        () => toSelectOptions(options.data_categories, t),
        [options.data_categories, t]
    );
    const methodOptions = useMemo(
        () => toSelectOptions(options.transfer_methods, t),
        [options.transfer_methods, t]
    );
    const relationOptions = useMemo(
        () => toSelectOptions(options.relations, t),
        [options.relations, t]
    );
    const restrictRelationToSyncs = isReceivesFromHardware(
        direction,
        form.selectedCounterpart
    );
    const visibleRelationOptions = useMemo(
        () =>
            withRelationOptionAvailability(relationOptions, {
                onlySyncs: restrictRelationToSyncs,
            }),
        [relationOptions, restrictRelationToSyncs]
    );

    useEffect(() => {
        if (!isOpen || !restrictRelationToSyncs) {
            return;
        }

        const nextRelation = resolveHardwareRelation(
            direction,
            form.selectedCounterpart,
            form.relation,
            relationOptions
        );

        if (nextRelation?.value !== form.relation?.value) {
            setForm((current) => ({
                ...current,
                relation: nextRelation,
            }));
        }
    }, [
        direction,
        form.relation,
        form.selectedCounterpart,
        isOpen,
        relationOptions,
        restrictRelationToSyncs,
    ]);

    useEffect(() => {
        if (!isOpen) {
            setForm(EMPTY_FORM);
            setErrors({});
        }
    }, [isOpen]);

    const updateForm = (patch) => {
        setForm((current) => ({
            ...current,
            ...patch,
        }));
    };

    const handleClose = () => {
        if (isSaving) {
            return;
        }

        onClose();
    };

    const handleSave = () => {
        if (isResolvingWrapper) {
            return;
        }

        if (!wrapperAssetId) {
            toast(t("Failed to create data flow."), {
                type: "error",
            });
            return;
        }

        if (!form.selectedCounterpart) {
            const field =
                direction === DIRECTION_RECEIVES ? "source" : "destination";

            setErrors((current) => ({
                ...current,
                [field]: t("A valid asset or endpoint reference is required"),
            }));
            return;
        }

        try {
            createFlow(
                buildCreatePayload({
                    direction,
                    wrapperAssetId,
                    counterpart: form.selectedCounterpart,
                    purpose: form.purpose,
                    notes: form.notes,
                    dataTypes: toOptionValues(form.dataTypes),
                    dataCategories: toOptionValues(form.dataCategories),
                    methods: toOptionValues(form.methods),
                    relation: form.relation?.value,
                })
            );
        } catch (error) {
            if (process.env.NODE_ENV === "development") {
                console.error(error);
            }

            toast(t("Failed to create data flow."), {
                type: "error",
            });
        }
    };

    const isReceives = direction === DIRECTION_RECEIVES;

    return (
        <Modal isOpen={isOpen} toggle={handleClose} centered size="lg">
            <ModalHeader toggle={isSaving ? undefined : handleClose}>
                <div>
                    <div>{t("Add Data Flow")}</div>
                    <small className="text-muted d-block">
                        {isReceives
                            ? t("Receives Data From")
                            : t("Sends Data To")}
                    </small>
                </div>
            </ModalHeader>
            <ModalBody>
                <CounterpartPicker
                    key={isOpen ? direction : "closed"}
                    wrapperAssetId={wrapperAssetId}
                    value={form.selectedCounterpart}
                    onChange={(selectedCounterpart) => {
                        updateForm({
                            selectedCounterpart,
                            relation: resolveHardwareRelation(
                                direction,
                                selectedCounterpart,
                                form.relation,
                                relationOptions
                            ),
                        });
                        setErrors((current) => ({
                            ...current,
                            source: null,
                            destination: null,
                            relation: null,
                        }));
                    }}
                    typeLabel={isReceives ? t("Source") : t("Destination")}
                    required
                    error={errors.source || errors.destination}
                    disabled={isSaving}
                    enabled={isOpen}
                />

                <DataFlowFormFields
                    purpose={form.purpose}
                    notes={form.notes}
                    dataTypes={form.dataTypes}
                    dataCategories={form.dataCategories}
                    methods={form.methods}
                    dataTypeOptions={dataTypeOptions}
                    dataCategoryOptions={dataCategoryOptions}
                    methodOptions={methodOptions}
                    errors={errors}
                    disabled={isSaving}
                    onPurposeChange={(purpose) => updateForm({ purpose })}
                    onNotesChange={(notes) => updateForm({ notes })}
                    onDataTypesChange={(dataTypes) => updateForm({ dataTypes })}
                    onDataCategoriesChange={(dataCategories) =>
                        updateForm({ dataCategories })
                    }
                    onMethodsChange={(methods) => updateForm({ methods })}
                    relation={form.relation}
                    relationOptions={visibleRelationOptions}
                    showRelation={isHardwareCounterpart(form.selectedCounterpart)}
                    onRelationChange={(relation) => updateForm({ relation })}
                />
            </ModalBody>
            <ModalFooter>
                <Button
                    color="secondary"
                    outline
                    type="button"
                    onClick={handleClose}
                    disabled={isSaving}
                >
                    {t("Cancel")}
                </Button>
                <Button
                    color="primary"
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving || isResolvingWrapper}
                >
                    {isSaving || isResolvingWrapper ? (
                        <Spinner size="sm" />
                    ) : (
                        t("Save Flow")
                    )}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default withNamespaces()(CreateDataFlowModal);
